import React from "react";
import { useStaticQuery, graphql } from "gatsby";
import {
  DefaultOpenGraphQuery,
  Maybe,
  OpenGraphMetaTagsFragment
} from "types/graphql-types";
import OpenGraph from "./OpenGraph";
import { Language } from "src/utils/localization/languages/types";
import { AlternateLink } from "src/utils/localization/alternateLinks";
import { pickTranslation } from "src/utils/localization/languages/languages";

interface DefaultOpenGraphProps {
  language: Language;
  alternateLinks: AlternateLink[];
}

/*
 *  SEO generate alternate links
 */
function generateAlternateLinks(alternateLinks: AlternateLink[]) {
  if (!alternateLinks || alternateLinks.length == 0) return null;

  return alternateLinks.map(({ ogLangCode, path }) => (
    <link key={ogLangCode} rel="alternate" hrefLang={ogLangCode} href={path} />
  ));
}

const DefaultOpenGraph: React.FC<DefaultOpenGraphProps> = ({
  language,
  alternateLinks
}) => {
  const data = useStaticQuery<DefaultOpenGraphQuery>(query);
  let localizedOpenGraph: OpenGraphMetaTagsFragment | undefined = undefined;
  if (data) {
    const openGraph = data.site?.openGraph;
    const title = pickTranslation<Maybe<string>>(openGraph?.title, language);
    const metaDescription = pickTranslation<Maybe<string>>(
      openGraph?.description,
      language
    );

    localizedOpenGraph = {
      title: { localized: title },
      description: { localized: metaDescription },
      image: openGraph?.image
    };
  }

  return (
    <OpenGraph
      openGraph={localizedOpenGraph}
      language={language}
      excludeFromSitemap={undefined}
    >
      {generateAlternateLinks(alternateLinks)}
    </OpenGraph>
  );
};

export default DefaultOpenGraph;

const query = graphql`
  query DefaultOpenGraph {
    site: sanitySite(_id: { eq: "site-silversea-com" }) {
      openGraph {
        title {
          en
          es
          de
          fr
          pt_br
        }

        description {
          en
          es
          de
          fr
          pt_br
        }

        image {
          ...Image
        }
      }
    }
  }
`;
